import { createContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ComingSoon from "@/pages/ComingSoon";

/**
 * Pre-launch gate. While pre-launch mode is on, visitors only see the coming-soon page;
 * the admin and test accounts see the full site. The server enforces the same rule on every
 * API call, so this is the friendly face of it, not the lock.
 */
// patch 30: the last answer is remembered, so returning visitors see the site at once (it is re-checked
// in the background), and the request starts while the app is still loading instead of after the first render.
const KEY = "tier_launch_v1";
function readCache() {
  try {
    const d = JSON.parse(localStorage.getItem(KEY) || "null");
    return d && typeof d.prelaunch === "boolean" ? d : null;
  } catch {
    return null;
  }
}
const fetchStatus = () => api.get("/launch/status").then(({ data }) => data);
let firstFetch = typeof window !== "undefined" ? fetchStatus() : null;
firstFetch?.catch(() => {});

/** {prelaunch, launch_at, instagram} for anything else that needs it (the footer). */
export const LaunchCtx = createContext(null);

export default function LaunchGate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [status, setStatus] = useState(readCache);

  useEffect(() => {
    // If the status can't be read (server unreachable), stay closed: retry a few times, then show the
    // coming-soon page rather than an empty site. The server enforces the gate either way.
    let alive = true;
    const tryLoad = (n) => {
      const p = n === 0 && firstFetch ? firstFetch : fetchStatus();
      firstFetch = null;
      p.then((data) => {
        if (!alive) return;
        setStatus(data);
        try {
          localStorage.setItem(KEY, JSON.stringify(data));
        } catch {
          /* storage blocked */
        }
      }).catch(() => {
        if (!alive) return;
        if (n < 3) setTimeout(() => tryLoad(n + 1), 1500 * (n + 1));
        else setStatus((s) => s || { prelaunch: true, unreachable: true });
      });
    };
    tryLoad(0);
    return () => { alive = false; };
  }, []);

  if (!status) return <div className="min-h-screen bg-[#0A0A0C]" />;
  return <LaunchCtx.Provider value={status}>{gate(status, user, pathname, children)}</LaunchCtx.Provider>;
}

function gate(status, user, pathname, children) {
  if (!status.prelaunch) return children;
  const inside = user && (user.is_admin || user.tester);
  const noindex = (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
  if (inside || pathname.startsWith("/admin")) {
    return (
      <>
        {noindex}
        {children}
      </>
    );
  }
  return (
    <>
      {noindex}
      <ComingSoon launchAt={status.launch_at} instagram={status.instagram} />
    </>
  );
}
